import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { keyframes, type Theme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";

import { salesApi } from "../client.js";
import {
  agentWork,
  HARVESTER_NAME,
  LEAD_GENERATOR_NAME,
  workMeta,
  type AgentWorkMeta,
  type SalesAgentTrack,
} from "../salesAgents.js";

// "Working now" requires RECENT activity, not just the mode switch being on —
// otherwise a harvester that finished its sweep would falsely read "Working".
// Generous thresholds: the harvester acts every ~90-120s, the lead agent every
// ~30s, so anything older than these means it isn't actually doing work.
const HARVESTER_STALE_S = 15 * 60;
const LEAD_STALE_S = 10 * 60;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.65); }
`;

const TRACKS: { track: SalesAgentTrack; name: string }[] = [
  { track: "lead_generator", name: LEAD_GENERATOR_NAME },
  { track: "harvester", name: HARVESTER_NAME },
];

function dotColor(tone: AgentWorkMeta["tone"], t: Theme): string {
  return tone === "default" ? t.palette.text.disabled : t.palette[tone].main;
}

function AgentPill({ name, meta }: { name: string; meta: AgentWorkMeta }): JSX.Element {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
      <Box
        aria-hidden
        sx={{
          flexShrink: 0,
          width: 9,
          height: 9,
          borderRadius: "50%",
          bgcolor: (t) => dotColor(meta.tone, t),
          boxShadow: (t) => `0 0 0 3px ${dotColor(meta.tone, t)}22`,
          animation: meta.live ? `${pulse} 1.4s ease-in-out infinite` : "none",
        }}
      />
      <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", whiteSpace: "nowrap" }}>
        {name}
      </Typography>
      <Tooltip title={meta.hint}>
        <Chip
          label={meta.label}
          size="small"
          color={meta.tone}
          variant={meta.live ? "filled" : "outlined"}
          sx={{ borderRadius: 1, fontWeight: 500, height: 22 }}
        />
      </Tooltip>
    </Box>
  );
}

/** Seconds since the newest item in the harvester activity feed (any
 * enumerate/dig/dedup action), or null if there's no activity to measure. Uses
 * the MAX ts so it's robust to feed ordering. */
function harvesterIdleSeconds(items: { ts: string }[] | undefined, nowMs: number): number | null {
  if (!items || items.length === 0) return null;
  let newest = 0;
  for (const it of items) {
    const t = new Date(it.ts).getTime();
    if (Number.isFinite(t) && t > newest) newest = t;
  }
  if (newest === 0) return null;
  return (nowMs - newest) / 1000;
}

/**
 * The always-visible "which AI agent is working now?" pills. Mounted at the far
 * RIGHT of the Sales tab (menu) row via `<SectionHeader tabsRight>` (2026-08-07
 * — it used to be its own labelled card below the nav; the "Your two AI agents"
 * caption is gone, the two named pills carry the meaning on their own). So on
 * EVERY Sales tab the operator sees, at a glance, both named agents — Lead
 * Generator + Harvester — and each one's live state (Working now / Standing by
 * / Paused / Resting) without the strip eating a row of page. Self-fetches
 * the shared agent-control + today queries the rest of the section uses, so it
 * can never disagree with the mode toggle or the Status panel.
 */
export function AgentWorkingNow(): JSX.Element {
  const ctl = useQuery({
    queryKey: ["sales", "agent-control"],
    queryFn: salesApi.getAgentControl,
    refetchInterval: 30_000,
    retry: false,
    placeholderData: (prev) => prev,
  });
  const today = useQuery({
    queryKey: ["sales", "today"],
    queryFn: salesApi.getToday,
    refetchInterval: 30_000,
    retry: false,
    placeholderData: (prev) => prev,
  });
  // Freshness — so "Working now" reflects ACTUAL recent activity, not just the
  // switch being on. Lead agent: the master-step age from health (shares the
  // pill's query). Harvester: the newest item in its activity feed.
  const health = useQuery({
    queryKey: ["sales", "health"],
    queryFn: salesApi.getHealth,
    refetchInterval: 30_000,
    retry: false,
    placeholderData: (prev) => prev,
  });
  const activity = useQuery({
    queryKey: ["sales", "harvest", "activity"],
    queryFn: salesApi.getHarvestActivity,
    refetchInterval: 30_000,
    retry: false,
    placeholderData: (prev) => prev,
  });

  const mode = ctl.data?.mode ?? "leads";
  const isPaused = ctl.data?.is_paused ?? false;
  const budgetExhausted = today.data?.budget_exhausted ?? false;

  // Stale = we have a POSITIVE freshness reading and it's beyond the threshold.
  // Missing data ⇒ not stale (stay optimistic), so the strip never invents an
  // "Idle" from a not-yet-loaded query.
  const leadAge = health.data?.last_master_step_age_s;
  const leadStale = typeof leadAge === "number" && leadAge > LEAD_STALE_S;
  const harvesterIdleS = harvesterIdleSeconds(activity.data?.items, Date.now());
  const harvesterStale = harvesterIdleS != null && harvesterIdleS > HARVESTER_STALE_S;
  const staleByTrack: Record<SalesAgentTrack, boolean> = {
    lead_generator: leadStale,
    harvester: harvesterStale,
  };

  return (
    <Box
      data-testid="agent-working-now"
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "nowrap",
        gap: 2,
      }}
    >
      {TRACKS.map(({ track, name }) => (
        <AgentPill
          key={track}
          name={name}
          meta={workMeta(
            agentWork(track, mode, { isPaused, budgetExhausted, stale: staleByTrack[track] }),
          )}
        />
      ))}
    </Box>
  );
}
